import styled from '@emotion/styled';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Dispatch } from 'src/config/rootStore';
import { getGoalNameForLanguage } from 'src/modules/goals/helpers/getGoalNameForLanguage';
import { getMainGoalCategoriesWithSubcategories } from 'src/modules/goals/selectors/goalsSelectors';
import { BrandWrapper } from 'src/modules/onboarding/components/BrandWrapper';
import { Button } from 'src/shared/components/Button';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

const Wrapper = styled.div({
    position: 'absolute',
    top: '20%',
    textAlign: 'center',
    width: '100%',
    padding: '1em',
    [mediaQueries.sm]: {
        top: '8%',
    },
});

const TextsWrapper = styled.div({
    position: 'relative',
    display: 'inline-block',
    padding: '0 15px 20px 15px',
});

const HeadingText = styled.h2(
    {
        color: color.pickledBluewood,
        marginBottom: '0.2em',
    },
    font.black(35),
);

const DescriptionText = styled.p(
    {
        color: color.steelGray,
        marginTop: '1em',
        marginBottom: '2em',
    },
    font.medium(font.size.medium),
);

const CategoriesList = styled.ul({
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: 900,
    paddingBottom: '2em',
    paddingLeft: 0,
    justifyContent: 'space-around',
    margin: '0 auto',
});

interface CategoryProps {
    active?: boolean;
    disabled?: boolean;
}

const Category = styled.li<CategoryProps>(props => ({
    opacity: props.disabled ? 0.6 : 1,
    position: 'relative',
    width: '48%',
    padding: '2em',
    border: '2.5px solid',
    borderColor: props.active ? color.cornflowerBlue : color.linkWaterLight,
    borderRadius: 6,
    marginBottom: '1em',
    cursor: 'pointer',
    textAlign: 'left',
    transition: '0.3s all',
    '&:hover': {
        borderColor: props.active ? color.cornflowerBlue : '#adc0f2',
    },
    [mediaQueries.md]: {
        width: '80%',
    },
    [mediaQueries.sm]: {
        width: '100%',
    },
}));

const CategoryName = styled.p(
    {
        color: color.fiord,
    },
    font.heavy(font.size.medium),
);

const CategoryImage = styled.img({
    position: 'absolute',
    width: 150,
    bottom: 0,
    right: 0,
});

type Props = RouteComponentProps;

export const ReTriageTopicsSelectionPage: React.FC<Props> = ({ history }) => {
    const { t, i18n } = useTranslation();
    const dispatch: Dispatch = useDispatch();

    const categories = useSelector(getMainGoalCategoriesWithSubcategories);

    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [saveInProgress, setSaveInProgress] = useState(false);

    const handleCategoryClick = (categoryId: number) => () => {
        const alreadySelected = selectedCategories.includes(categoryId);

        if (saveInProgress) {
            return;
        }

        // can't select more than 3
        if (!alreadySelected && selectedCategories.length === 3) {
            return;
        }

        const newSelectedCategories = alreadySelected
            ? selectedCategories.filter(id => id !== categoryId)
            : [...selectedCategories, categoryId];

        setSelectedCategories(newSelectedCategories);
    };

    const handleSubmit = async () => {
        if (saveInProgress) {
            return;
        }

        setSaveInProgress(true);

        await dispatch.goalInterests.saveGoalInterests(selectedCategories);

        setSaveInProgress(false);

        history.push('/re-triage-assessment');
    };

    return (
        <BrandWrapper>
            <Wrapper>
                <TextsWrapper>
                    <HeadingText>{t('retriage:select_topics_page.title')}</HeadingText>
                    <DescriptionText>
                        {t('retriage:select_topics_page.description')}
                    </DescriptionText>
                </TextsWrapper>

                <CategoriesList>
                    {categories.map(category => (
                        <Category
                            key={category.id}
                            active={selectedCategories.includes(category.id)}
                            disabled={
                                selectedCategories.length === 3 &&
                                !selectedCategories.includes(category.id)
                            }
                            onClick={handleCategoryClick(category.id)}
                        >
                            <CategoryName>
                                {getGoalNameForLanguage(category, i18n.language)}
                            </CategoryName>
                            <CategoryImage src={category.image} />
                        </Category>
                    ))}
                </CategoriesList>

                <Button
                    onClick={handleSubmit}
                    type="accept"
                    loading={saveInProgress}
                    disabled={!selectedCategories.length}
                    withShadow={!!selectedCategories.length}
                >
                    {t('common.continue')}
                </Button>
            </Wrapper>
        </BrandWrapper>
    );
};
